import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';
import { leadGenService } from '../../services/leadGeneration';
import { crawlingService } from '../../services/crawlingService';
import { intelligentSearchService } from '../../services/IntelligentSearchService';
import toast from 'react-hot-toast';
import Loading from '../common/Loading';
import GuideSystem from '../common/GuideSystem';
import FloatingGuideButton from '../common/FloatingGuideButton';
import { getGuideContent } from '../../utils/guideContent';
import { useGuide } from '../../hooks/useGuide';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, signOut, isAdmin, getRemainingRequests, getRequestLimit, getUserType, permissions } = useAuth();
  const [stats, setStats] = useState(null);
  const [crawlingStats, setCrawlingStats] = useState(null);
  const [intelligentStats, setIntelligentStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFirstTimeMessage, setShowFirstTimeMessage] = useState(false);
  
  // Guide system integration
  const { isGuideVisible, hasSeenGuide, showGuide, hideGuide } = useGuide('dashboard');
  const guideData = getGuideContent('dashboard');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
  setLoading(true);
  
  try {
    // Load lead generation stats
    const { data: leadStats } = await leadGenService.getSearchStats();
    if (leadStats) {
      setStats(leadStats);
    }

    // Load crawling stats
    const { data: crawlStats } = await crawlingService.getCrawlStats();
    if (crawlStats) {
      setCrawlingStats(crawlStats);
    }

    // Load intelligent search stats (only if user has access)
    let intelligentData = null;
    if (permissions.canAccessIntelligentSearch) {
      const { data } = await intelligentSearchService.getIntelligentSearchStats();
      if (data) {
        setIntelligentStats(data);
        intelligentData = data;
      }
    }

    // Check if this is a first-time user (no activity at all)
    const isFirstTime = (!leadStats || leadStats.total_search_sessions === 0) && 
                       (!crawlStats || crawlStats.total_tasks === 0) && 
                       (!intelligentData || intelligentData.total_areas === 0);
    
    // Show first-time message for guest users who are new
    const userType = getUserType();
    if (userType === 'guest' && isFirstTime) {
      setShowFirstTimeMessage(true);
      // Auto-hide after 10 seconds if user doesn't interact
      setTimeout(() => {
        setShowFirstTimeMessage(false);
      }, 10000);
    }

  } catch (error) {
    console.error('Error loading dashboard data:', error);
  }
  
  setLoading(false);
};

  const handleSignOut = async () => {
    const { success } = await signOut();
    if (success) {
      navigate('/login');
    }
  };

  const handleDismissFirstTimeMessage = () => {
    setShowFirstTimeMessage(false);
  };

  const handleShowGuide = () => {
    setShowFirstTimeMessage(false);
    showGuide();
  };

  const StatCard = ({ title, value, subtitle, icon, color = 'blue', onClick }) => (
    <div 
      className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center">
        <div className={`p-2 rounded-lg bg-${color}-100`}>
          <span className="text-lg">{icon}</span>
        </div>
        <div className="ml-3 flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-600 truncate">{title}</p>
          <p className="text-xl font-bold text-gray-900">{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-500 truncate">{subtitle}</p>
          )}
        </div>
      </div>
    </div>
  );

  const NavigationCard = ({ title, description, icon, color = 'blue', onClick, badge }) => (
    <div 
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md transition-shadow"
      onClick={onClick}
    >
      <div className="flex items-start">
        <div className={`p-3 rounded-lg bg-${color}-100 flex-shrink-0`}>
          <span className="text-2xl">{icon}</span>
        </div>
        <div className="ml-4 flex-1">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-medium text-gray-900">{title}</h3>
            {badge && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {badge}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600">{description}</p>
        </div>
        <div className="ml-4 flex-shrink-0">
          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <Loading />;
  }

  const isNewUser = !stats || (stats.total_search_sessions === 0 && (!intelligentStats || intelligentStats.total_areas === 0));
  const userType = getUserType();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Guide System */}
      {guideData && (
        <GuideSystem
          steps={guideData.steps}
          pageName={guideData.pageName}
          isVisible={isGuideVisible}
          onClose={hideGuide}
        />
      )}

      {/* Floating Guide Button */}
      <FloatingGuideButton 
        onClick={showGuide}
        hasNewFeature={!hasSeenGuide}
      />

      {/* First Time Guest User Message */}
      {showFirstTimeMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative animate-pulse">
            <button
              onClick={handleDismissFirstTimeMessage}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                <span className="text-2xl">👋</span>
              </div>
              
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Welcome to Lead Generation!
              </h3>
              
              <p className="text-sm text-gray-600 mb-4">
                New here? Don't know where to start?
              </p>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <span className="text-lg">💡</span>
                  </div>
                  <div className="ml-2 text-left">
                    <p className="text-sm font-medium text-yellow-800">
                      Click the Help button (?) in the bottom right
                    </p>
                    <p className="text-xs text-yellow-700">
                      It will guide you through all the features step by step!
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={handleShowGuide}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                >
                  Show Me Around 🚀
                </button>
                <button
                  onClick={handleDismissFirstTimeMessage}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
                >
                  I'll Explore
                </button>
              </div>
              
              <p className="text-xs text-gray-500 mt-3">
                As a guest user, you have {getRemainingRequests()} free searches to try out the app!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-screen mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Dashboard</h1>
              <div className="flex items-center space-x-2">
                <p className="text-sm text-gray-600">Welcome back, {user?.displayName || user?.email}</p>
                {userType === 'guest' && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    Guest Account
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {userType === 'guest' && (
                <button
                  onClick={() => navigate('/login')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                >
                  Login
                </button>
              )}
              <button
                onClick={() => navigate('/profile')}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
              >
                Profile
              </button>
              <button
                onClick={handleSignOut}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Getting Started for New Users */}
        {isNewUser && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-base font-medium text-blue-900 mb-1">
                  Welcome to the Lead Generation App!
                </h3>
                <p className="text-sm text-blue-700 mb-3">
                  Get started by choosing a search method below. {permissions.canAccessIntelligentSearch ? 'Use Intelligent Search for AI optimization or Manual Search for precise control.' : 'Start with Manual Search to find businesses in your target areas.'}
                </p>
                <div className="flex space-x-3">
                  {permissions.canAccessIntelligentSearch && (
                    <button
                      onClick={() => navigate('/intelligent-search')}
                      className="px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm font-medium"
                    >
                      Try Intelligent Search →
                    </button>
                  )}
                  <button
                    onClick={() => navigate('/search')}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                  >
                    Try Manual Search →
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Overview Stats */}
        <div className="mb-8">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Overview</h2>
          <div className={`grid grid-cols-1 md:grid-cols-2 ${permissions.canAccessIntelligentSearch ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-4`}>
            <StatCard
              title="API Requests Remaining"
              value={getRemainingRequests()}
              subtitle={`of ${getRequestLimit()} total`}
              icon="🔄"
              color="blue"
            />
            <StatCard
              title="Places Found"
              value={stats?.total_places_found || 0}
              subtitle="Total discovered"
              icon="📍"
              color="green"
              onClick={() => navigate('/places')}
            />
            <StatCard
              title="Active Leads"
              value={stats?.total_leads_created || 0}
              subtitle="In pipeline"
              icon="🎯"
              color="purple"
              onClick={() => navigate('/leads')}
            />
            {permissions.canAccessIntelligentSearch && (
              <StatCard
                title="AI Search Areas"
                value={intelligentStats?.total_areas || 0}
                subtitle={`${intelligentStats?.ready_areas || 0} ready`}
                icon="🤖"
                color="indigo"
                onClick={() => navigate('/intelligent-search/dashboard')}
              />
            )}
            <StatCard
              title="Social Links"
              value={crawlingStats?.total_social_links || 0}
              subtitle={`${crawlingStats?.verified_links || 0} verified`}
              icon="🔗"
              color="orange"
              onClick={() => navigate('/crawling/links')}
            />
          </div>
        </div>

       {/* Main Actions */}
       <div className="mb-8">
         <h2 className="text-lg font-medium text-gray-900 mb-4">Main Actions</h2>
         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
           <NavigationCard
             title="Search Places"
             description="Find businesses using custom map areas with circle placement"
             icon="🔍"
             color="blue"
             onClick={() => navigate('/search')}
           />
           {permissions.canAccessIntelligentSearch && (
             <>
               <NavigationCard
                 title="Intelligent Search"
                 description="AI-powered location optimization for efficient business discovery"
                 icon="🤖"
                 color="indigo"
                 onClick={() => navigate('/intelligent-search')}
                 badge={intelligentStats?.pending_areas > 0 ? `${intelligentStats.pending_areas} pending` : 'AI-Powered'}
               />
               <NavigationCard
                 title="Intelligent Dashboard"
                 description="Manage and monitor your AI-optimized search areas"
                 icon="📊"
                 color="indigo"
                 onClick={() => navigate('/intelligent-search/dashboard')}
                 badge={intelligentStats?.total_areas > 0 ? intelligentStats.total_areas : null}
               />
             </>
           )}
           <NavigationCard
             title="Manage Saved Places"
             description="View, organize and convert your saved places into leads"
             icon="📋"
             color="green"
             onClick={() => navigate('/places')}
             badge={stats?.total_places_found > 0 ? stats.total_places_found : null}
           />
           <NavigationCard
             title="Crawl Social Media"
             description="Automatically discover social media profiles and contact information"
             icon="🕷️"
             color="purple"
             onClick={() => navigate('/crawling')}
             badge={crawlingStats?.pending_tasks > 0 ? `${crawlingStats.pending_tasks} active` : null}
           />
           <NavigationCard
             title="Manage Leads"
             description="Track your outreach efforts and lead conversion progress"
             icon="🎯"
             color="orange"
             onClick={() => navigate('/leads')}
             badge={stats?.total_leads_created > 0 ? stats.total_leads_created : null}
           />
           <NavigationCard
             title="View Statistics"
             description="Analyze your lead generation performance and crawling results"
             icon="📊"
             color="pink"
             onClick={() => navigate('/crawling/stats')}
           />
           {isAdmin() && (
             <NavigationCard
               title="Admin Panel"
               description="Manage users, view system stats and configure crawling settings"
               icon="⚙️"
               color="gray"
               onClick={() => navigate('/admin')}
               badge="Admin"
             />
           )}
         </div>
       </div>
     </div>
   </div>
 );
};

export default Dashboard;